import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { canonicalRole } from './utils/roles';
import AdminLayout from './components/AdminLayout';

// Lazy load components
const PublicEstimateAccept = lazy(() => import('./pages/PublicEstimateAccept'));
const PublicInvoiceReceipt = lazy(() => import('./pages/PublicInvoiceReceipt'));
const PublicPaymentReceipt = lazy(() => import('./pages/PublicPaymentReceipt'));
const StripeReturn = lazy(() => import('./pages/StripeReturn'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Blog = lazy(() => import('./pages/Blog'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
// /demo signs in with a shared scanner@demo.com password printed on the page.
// Development builds only: CRA inlines NODE_ENV at build time, so in production
// this import is dead code and the chunk (and the password) never ships.
const DemoLogin = process.env.NODE_ENV !== 'production'
  ? lazy(() => import('./pages/DemoLogin'))
  : null;
// pages/Today.js (the old attention-queue Today, cfo365-redesign.md §6) is not
// routed since 2026-09-10 — /today renders MoneyDashboard.
// EmployerDashboard is now the TIMESHEET surface only, served at /admin/timesheet.
// It keeps the full-bleed live map header, so it stays outside AdminLayout.
const EmployerDashboard = lazy(() => import('./pages/EmployerDashboard'));
// File rename deferred — UserDashboard is still EmployeeDashboard.js on disk
const UserDashboard = lazy(() => import('./pages/EmployeeDashboard'));
const ScannerDashboard = lazy(() => import('./pages/ScannerDashboard'));
const CompanyAdmin = lazy(() => import('./pages/CompanyAdmin'));
const AdminSandbox = lazy(() => import('./pages/AdminSandbox'));
const Jobs = lazy(() => import('./pages/Jobs'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const JobCreate = lazy(() => import('./pages/JobCreate'));
// The money dashboard IS Today (founder, 2026-09-10: "Money is Today. 1st in
// line"): the bank-account chart + ledger are the front page at /today; Finance
// (invoices/estimates/payments tables) is the Billing drill-down at /money/billing.
const MoneyDashboard = lazy(() => import('./pages/MoneyDashboard'));
const Ledger = lazy(() => import('./pages/Ledger'));
const Finance = lazy(() => import('./pages/Finance'));
const ProposalDetail = lazy(() => import('./pages/ProposalDetail'));
const ProposalCreate = lazy(() => import('./pages/ProposalCreate'));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail'));
const InvoiceCreate = lazy(() => import('./pages/InvoiceCreate'));
const CompanySettings = lazy(() => import('./pages/CompanySettings'));
const Approvals = lazy(() => import('./pages/Approvals'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Customers = lazy(() => import('./pages/Customers'));
const Crew = lazy(() => import('./pages/Crew'));
// More — utilities/account/settings hub (cfo365-redesign.md §8)
const More = lazy(() => import('./pages/More'));

// Loading component
const LoadingSpinner = () => (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-text-muted">Loading...</p>
        </div>
      </div>
    );

// canonicalRole (legacy → canon role translation) is imported from ./utils/roles.

// Protected Route component
// requiredRole: single string (legacy). requiredRoles: string[] for multi-role access.
const ProtectedRoute = ({ children, requiredRole, requiredRoles, user }) => {
  if (!user) {
    if (process.env.NODE_ENV !== 'production') console.log('No user found, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  const role = canonicalRole(user.role);

  // Multi-role check
  const allowedRoles = requiredRoles || (requiredRole ? [requiredRole] : []);
  const allowed = allowedRoles.length === 0 || allowedRoles.includes(role);

  if (!allowed) {
    if (process.env.NODE_ENV !== 'production') console.log(`User role ${role} doesn't match required role(s) [${allowedRoles.join(', ')}], redirecting`);
    // Super admins can access company admin but should redirect to super-admin if they try other routes
    if (allowedRoles.includes('super-admin') || role === 'super-admin') {
      return role === 'super-admin' ? children : <Navigate to="/super-admin" replace />;
    }
    return <Navigate to={`/${role}`} replace />;
  }

  return children;
};

// ─── Legacy path redirect ────────────────────────────────────────────────────
//
// The 2026-08-30 IA rename (cfo365-redesign.md §2.1) moved /dashboard, /jobs,
// /finance, /approvals, /calendar and /customers under the four new
// destinations. These redirects are PERMANENT, not transitional: bookmarks,
// emailed links and any external reference to the old paths must keep working.
//
// `to` may contain :params, which are substituted from the matched route.
// Query string and hash are preserved so links like /finance?tab=proposals
// survive the move.
const LegacyRedirect = ({ to }) => {
  const params = useParams();
  const { search, hash } = useLocation();
  const target = to.replace(/:([A-Za-z0-9_]+)/g, (_, key) => params[key] ?? '');
  return <Navigate to={`${target}${search}${hash}`} replace />;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (process.env.NODE_ENV !== 'production') console.log('AppRoutes render:', { user: user ? { uid: user.uid, role: user.role } : null, loading });

  // Auto-refresh the app on an hourly cadence (configurable via env)
  useEffect(() => {
    const minutes = parseInt(process.env.REACT_APP_AUTO_REFRESH_MINUTES || '60', 10);
    if (!minutes || minutes <= 0) return;
    const intervalMs = minutes * 60 * 1000;
    const id = setInterval(() => {
      // Only refresh when tab is visible to avoid disrupting background sessions
      if (document.visibilityState === 'visible') {
        window.location.reload();
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {process.env.NODE_ENV !== 'production' && (
          <Route
            path="/dev/sandbox"
            element={
              <ProtectedRoute requiredRole="super-admin" user={user}>
                <AdminSandbox />
              </ProtectedRoute>
            }
          />
        )}
        {/* Root route - show landing page for non-authenticated users */}
        <Route
          path="/"
          element={
            user ? (
              canonicalRole(user.role) === 'super-admin' ? (
                <Navigate to="/super-admin" replace />
              ) : canonicalRole(user.role) === 'admin' ? (
                <Navigate to="/today" replace />
              ) : (
                <Navigate to={`/${canonicalRole(user.role)}`} replace />
              )
            ) : <LandingPage />
          }
        />

        {/* Public routes */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />

        {/* Public estimate accept page — unauthenticated, token-gated */}
        <Route path="/v/est/:proposalId" element={<PublicEstimateAccept />} />
        <Route path="/v/inv/:invoiceId" element={<PublicInvoiceReceipt />} />
        <Route path="/v/r/:paymentId" element={<PublicPaymentReceipt />} />
        <Route path="/stripe-return" element={<StripeReturn />} />

        {/* Login routes */}
        <Route
          path="/login"
          element={
            user ? (
              canonicalRole(user.role) === 'admin' ? (
                <Navigate to="/today" replace />
              ) : canonicalRole(user.role) === 'super-admin' ? (
                <Navigate to="/super-admin" replace />
              ) : (
                <Navigate to={`/${canonicalRole(user.role)}`} replace />
              )
            ) : <Login />
          }
        />
        {DemoLogin && (
          <Route
            path="/demo"
            element={
              user ? (
                canonicalRole(user.role) === 'admin' ? (
                  <Navigate to="/today" replace />
                ) : canonicalRole(user.role) === 'super-admin' ? (
                  <Navigate to="/super-admin" replace />
                ) : (
                  <Navigate to={`/${canonicalRole(user.role)}`} replace />
                )
              ) : <DemoLogin />
            }
          />
        )}
        <Route
          path="/signup"
          element={
            user && !user.needsCompanyAssignment ? (
              canonicalRole(user.role) === 'admin' ? (
                <Navigate to="/today" replace />
              ) : canonicalRole(user.role) === 'super-admin' ? (
                <Navigate to="/super-admin" replace />
              ) : (
                <Navigate to={`/${canonicalRole(user.role)}`} replace />
              )
            ) : <Signup />
          }
        />
        
        {/* Protected dashboard routes */}
        {/* /recruiter is gone — redirect legacy bookmarks to /dashboard */}
        <Route path="/recruiter" element={<Navigate to="/today" replace />} />
        {/* /employer is gone — redirect legacy bookmarks to /dashboard */}
        <Route path="/employer" element={<Navigate to="/today" replace />} />
        {/* /admin is the old landing — redirect to /dashboard for the new layout */}
        <Route path="/admin" element={<Navigate to="/today" replace />} />
        {/* /employee is gone — redirect legacy bookmarks to /user */}
        <Route path="/employee" element={<Navigate to="/user" replace />} />

        {/* ── Legacy path redirects (IA rename 2026-08-30) — permanent ──────── */}
        <Route path="/dashboard" element={<LegacyRedirect to="/today" />} />
        <Route path="/approvals" element={<LegacyRedirect to="/projects/approvals" />} />
        <Route path="/calendar" element={<LegacyRedirect to="/projects/calendar" />} />
        <Route path="/customers" element={<LegacyRedirect to="/projects/customers" />} />
        <Route path="/jobs" element={<LegacyRedirect to="/projects" />} />
        <Route path="/jobs/new" element={<LegacyRedirect to="/projects/new" />} />
        <Route path="/jobs/:jobId" element={<LegacyRedirect to="/projects/:jobId" />} />
        <Route path="/finance" element={<LegacyRedirect to="/money/billing" />} />
        <Route path="/finance/proposals/new" element={<LegacyRedirect to="/money/proposals/new" />} />
        <Route path="/finance/proposals/:proposalId" element={<LegacyRedirect to="/money/proposals/:proposalId" />} />
        <Route path="/finance/invoices/new" element={<LegacyRedirect to="/money/invoices/new" />} />
        <Route path="/finance/invoices/:invoiceId" element={<LegacyRedirect to="/money/invoices/:invoiceId" />} />

        {/* ── Today became the money dashboard (founder, 2026-09-10) — permanent ── */}
        {/* Bookmarks to /money land on Today. The dashboard ignores ?tab=, so list links go to /money/billing?tab=…. */}
        <Route path="/money" element={<LegacyRedirect to="/today" />} />
        <Route path="/today/approvals" element={<LegacyRedirect to="/projects/approvals" />} />

        {/* ── Admin shell (AdminLayout wraps all admin routes via Outlet) ───── */}
        {/*
          AdminLayout renders the left rail only when role === 'admin'.
          Routes for /jobs/:jobId and /finance/invoices/:invoiceId are also nested here
          so admin users see the rail; AdminLayout hides the rail for non-admin users
          who reach those routes (user role can see job/invoice detail, read-only).
        */}
        <Route
          element={
            <ProtectedRoute requiredRoles={['admin', 'user']} user={user}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          {/* admin-only routes — AdminLayout gates these internally */}
          <Route
            path="/projects/customers"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <Customers />
              </ProtectedRoute>
            }
          />
          {/* Crews — the web twin of mobile Team → Crew. */}
          <Route
            path="/projects/crew"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <Crew />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <JobCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/calendar"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} user={user}>
                <Calendar />
              </ProtectedRoute>
            }
          />
          {/* Approvals left Today when Today became the money dashboard (2026-09-10). */}
          <Route
            path="/projects/approvals"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <Approvals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/money/ledger"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <Ledger />
              </ProtectedRoute>
            }
          />
          {/* Billing keeps the both-roles gate the old /finance route had — a
              non-admin user may view invoices for jobs they are assigned to. */}
          <Route
            path="/money/billing"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} user={user}>
                <Finance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/money/proposals/new"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <ProposalCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/money/proposals/:proposalId"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <ProposalDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/money/invoices/new"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <InvoiceCreate />
              </ProtectedRoute>
            }
          />
          {/* Both roles — admin manages, user can view + pay for assigned jobs */}
          <Route path="/projects/:jobId" element={<JobDetail />} />
          <Route path="/money/invoices/:invoiceId" element={<InvoiceDetail />} />
          <Route
            path="/today"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <MoneyDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/more"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <More />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <CompanySettings />
              </ProtectedRoute>
            }
          />
        </Route>



        {/* Legacy /admin timesheet view — accessed directly from timePlaceholder "Go to timesheet" */}
        <Route
          path="/admin/timesheet"
          element={
            <ProtectedRoute requiredRole="admin" user={user}>
              <EmployerDashboard user={user} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user"
          element={
            <ProtectedRoute requiredRole="user" user={user}>
              <UserDashboard user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scanner"
          element={
            <ProtectedRoute requiredRole="scanner" user={user}>
              <ScannerDashboard user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute requiredRole="super-admin" user={user}>
              <CompanyAdmin user={user} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies"
          element={
            <ProtectedRoute requiredRole="super-admin" user={user}>
              <CompanyAdmin user={user} />
            </ProtectedRoute>
          }
        />

        {/* Catch all route */}
        <Route
          path="*"
          element={
            user ? (
              canonicalRole(user.role) === 'admin' ? (
                <Navigate to="/today" replace />
              ) : canonicalRole(user.role) === 'super-admin' ? (
                <Navigate to="/super-admin" replace />
              ) : (
                <Navigate to={`/${canonicalRole(user.role)}`} replace />
              )
            ) : <Navigate to="/login" replace />
          }
        />
      </Routes>
      </Suspense>
    </Router>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
